import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  IconButton,
  Snackbar,
  Avatar,
  Card,
  Divider
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Delete,
  Send,
  Visibility,
  Add,
  Assignment,
  Cancel
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { decryptAppId, decryptText, encryptText, encryptAppId } from "../../../utils/encryption";
import axios from 'axios';
import { generateCommunicationLetter } from '../../../components/certificate/CommunicationLetterGenerator';
import CommonAPIService from '../../../services/commonAPIService';


const DecisionProcess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Get encrypted parameters from URL
  const encryptedAppId = searchParams.get("appId");
  const encryptedOrgId = searchParams.get("orgId");
  const encryptedAssessmentId = searchParams.get("assessmentId");
  const encryptedStatus = searchParams.get("status");
  const encryptedAssessmentTypeId = searchParams.get("assessmentTypeId");

  // Decrypt parameters
  const appIdFromUrl = encryptedAppId ? decryptAppId(encryptedAppId) : null;
  const orgIdFromUrl = encryptedOrgId ? decryptText(encryptedOrgId) : null;
  const assessmentIdFromUrl = encryptedAssessmentId ? decryptText(encryptedAssessmentId) : null;
  const statusFromUrl = encryptedStatus ? decryptText(encryptedStatus) : null;
  const assessmentTypeIdFromUrl = encryptedAssessmentTypeId ? decryptText(encryptedAssessmentTypeId) : null;
  
  // State management
  const [error, setError] = useState(null);
  const [appId] = useState(appIdFromUrl);
  const [orgId] = useState(orgIdFromUrl);
  const [assessmentId] = useState(assessmentIdFromUrl);
  const [ddlStatus, setDdlStatus] = useState(statusFromUrl || '');
  const [assessmentTypeId] = useState(assessmentTypeIdFromUrl);
  
  // Application data
  const [applicationData, setApplicationData] = useState(null);

  // Form states
  const [communicationSubject, setCommunicationSubject] = useState('');
  const [communicationBody, setCommunicationBody] = useState('');
  const [communicationDate, setCommunicationDate] = useState('');

  // Advisory/Showcause list
  const [advisoryList, setAdvisoryList] = useState([]);
  const [advisoryText, setAdvisoryText] = useState('');
  const [editingAdvisoryId, setEditingAdvisoryId] = useState(null);
  const [showcauseText, setShowcauseText] = useState('');
  const [editingShowcauseId, setEditingShowcauseId] = useState(null);

  // Conditional visibility states
  const [showAdvisorySection, setShowAdvisorySection] = useState(false);
  const [showShowcauseSection, setShowShowcauseSection] = useState(false);
  
  // Modal states
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [password, setPassword] = useState('');
  const [showSendLetterModal, setShowSendLetterModal] = useState(false);
  const [sendLetterPassword, setSendLetterPassword] = useState('');
  
  // Button visibility
  const [showSubmitButton, setShowSubmitButton] = useState(true);
  const [showViewLetterButton, setShowViewLetterButton] = useState(false);
  const [showSendLetterButton, setShowSendLetterButton] = useState(false);
  
  // Snackbar State
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  useEffect(() => {
    if (appId) {
      initializePage();
    }
  }, [appId]);

  const initializePage = async () => {
    try {
      // Load application details
      await loadApplicationDetails();
      
      // Load decision data from API
      await loadDecisionData();
      
      // Load advisory list from API
      await loadAdvisoryList();
     
    } catch (err) {
      console.error('Error initializing page:', err);
      setError(err.message || 'Failed to load page data');
    }
  };

  const loadApplicationDetails = async () => {
    try {
      // Make parallel API calls to get application details, lab head and DO details
      const [appDetailsResponse, labHeadResponse, doResponse] = await Promise.all([
        // Get application details (ApplicationNo, LaboratoryName, etc.)
        axios.get(`${process.env.REACT_APP_API_URL}/ac-process/application/details/${appId}`),
        // Get lab head/contact details (mode=27)
        axios.get(`${process.env.REACT_APP_API_URL}/do/allotment-details`, {
          params: {
            mode: 27,
            appId: appId
          }
        }),
        // Get DO details (mode=31)
        axios.get(`${process.env.REACT_APP_API_URL}/do/allotment-details`, {
          params: {
            mode: 31,
            appId: appId
          }
        })
      ]);

      // Extract data from responses
      const appData = appDetailsResponse.data?.data || {};
      const labHeadData = labHeadResponse.data?.data?.[0] || {};
      const doData = doResponse.data?.data?.[0] || {};

      // Combine data
      const appDetails = {
        ApplicationNo: appData.ApplicationNo || 'N/A',
        LaboratoryName: appData.LaboratoryName || 'N/A',
        ContactPerson: labHeadData.name || 'N/A',
        Address: labHeadData.address || 'N/A',
        ExtAddress: `${labHeadData.city || 'N/A'} ${labHeadData.state || 'N/A'} ${labHeadData.pincode || 'N/A'}`.trim(),
        ContactMobile: labHeadData.mobile || 'N/A',
        ContactEmail: labHeadData.email || 'N/A',
        DoName: doData.name || 'N/A',
        DoEmail: doData.email || 'N/A',
        Stage: appData.Stage || 'N/A',
        CommunicationLetterSent: appData.CommunicationLetterSent || 0,
      };

      setApplicationData(appDetails);

      setShowAdvisorySection(
        (ddlStatus === 'Accreditation With Advisory' && appDetails?.Stage === '7g') ||
        (ddlStatus === 'Accredited with advisory including show cause notice' && appDetails?.Stage === '13e')
      );
      setShowShowcauseSection(
        (ddlStatus === 'Show Cause' && appDetails?.Stage === '7h') ||
        (ddlStatus === 'Accredited with advisory including show cause notice' && appDetails?.Stage === '13e')
      );

      if(appDetails?.CommunicationLetterSent == 1) {
         setShowSubmitButton(false);
         setShowSendLetterButton(false);
      }
      console.log('Application details loaded successfully:', appDetails);
    } catch (err) {
      console.error('Error loading application details:', err);
    }
  };

  const loadDecisionData = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_DOT_NET_CORE_API_URL}/GetCommunication`, {
        params: {
          appid: appId,
          orgid: orgId
        }
      });
      
      if (response.data) {
        const subject = response.data.subject || '';
        const body = response.data.body || '';
        setCommunicationSubject(subject);
        setCommunicationBody(body);
        setCommunicationDate(response.data.date || '');
        console.log('Communication data loaded successfully');
        
        // Update button visibility based on whether data exists
        updateButtonVisibility(subject, body);
      }
    } catch (err) {
      console.error('Error loading decision data:', err);
    }
  };

  // Update button visibility based on communication data
  const updateButtonVisibility = (subject, body) => {
    // Show view and send buttons only if both subject and body are saved
    if (subject && subject.trim() && body && body.trim()) {
      setShowViewLetterButton(true);
      setShowSendLetterButton(true);
      setShowSubmitButton(true);
    } else {
      setShowViewLetterButton(false);
      setShowSendLetterButton(false);
      setShowSubmitButton(true);
    }
  };

  const loadAdvisoryList = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_DOT_NET_CORE_API_URL}/Getadvisory`, {
        params: {
          appid: appId,
          updatedby: localStorage.getItem('orgId') || '0',
        }
      });
      if (response.data && Array.isArray(response.data.advisory)) {
        setAdvisoryList(response.data.advisory);
        console.log('Advisory list loaded successfully');
      }
    } catch (err) {
      console.error('Error loading advisory list:', err);
    }
  };

  const handleAddAdvisory = async () => {
    if (!advisoryText.trim()) {
      showSnackbar('Please enter advisory text', 'warning');
      return;
    }

    try {
      const response = await axios.post(`${process.env.REACT_APP_DOT_NET_CORE_API_URL}/Saveadvisory`, {
        id: "0",
        appId: appId,
        description: advisoryText.trim(),
        labtypeid: applicationData?.LabTypeId || "0",
        updatedby: localStorage.getItem('orgId') || '0',
        advisoryType: 'Advisory',
        type: 'Add'
      });

      if (response.data && response.data.isstatus) {
        showSnackbar('Advisory added successfully', 'success');
        setAdvisoryText('');
        // Reload advisory list
        await loadAdvisoryList();
      }else {
        showSnackbar('Failed to add advisory', 'error');
      }
    } catch (err) {
      console.error('Error adding advisory:', err);
      showSnackbar('Failed to add advisory', 'error');
    }
  };

  const handleEditAdvisory = (advisory) => {
    setAdvisoryText(advisory.description);
    setEditingAdvisoryId(advisory.id);
  };

  const cancelEditAdvisory = () => {
     setAdvisoryText('');
     setEditingAdvisoryId(null);
  };

  const handleUpdateAdvisory = async () => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_DOT_NET_CORE_API_URL}/Saveadvisory`, {
        id: editingAdvisoryId.toString(),
        appId: appId,
        description: advisoryText.trim(),
        labtypeid: applicationData?.LabTypeId || "0",
        updatedby: localStorage.getItem('orgId') || '0',
        advisoryType: 'Advisory',
        type: 'Update'
      });

      if (response.data && response.data.isstatus) {
        showSnackbar('Advisory updated successfully', 'success');
        setAdvisoryText('');
        setEditingAdvisoryId(null);
        // Reload advisory list
        await loadAdvisoryList();
      } else {
        showSnackbar('Failed to update advisory', 'error');
      }
    } catch (err) {
      console.error('Error updating advisory:', err);
      showSnackbar('Failed to update advisory', 'error');
    }
  };

  const handleAddShowcause = async () => {
    if (!showcauseText.trim()) {
      showSnackbar('Please enter showcause text', 'warning');
      return;
    }

    try {
      const response = await axios.post(`${process.env.REACT_APP_DOT_NET_CORE_API_URL}/Saveadvisory`, {
        id: "0",
        appId: appId,
        description: showcauseText.trim(),
        labtypeid: applicationData?.LabTypeId || "0",
        updatedby: localStorage.getItem('orgId') || '0',
        advisoryType: 'ShowCause',
        type: 'Add'
      });

      if (response.data && response.data.isstatus) {
        showSnackbar('Showcause added successfully', 'success');
        setShowcauseText('');
        await loadAdvisoryList();
      } else {
        showSnackbar('Failed to add showcause', 'error');
      }
    } catch (err) {
      console.error('Error adding showcause:', err);
      showSnackbar('Failed to add showcause', 'error');
    }
  };

  const handleEditShowcause = (showcause) => {
    setShowcauseText(showcause.description);
    setEditingShowcauseId(showcause.id);
  };

  const cancelEditShowcause = () => {
    setShowcauseText('');
    setEditingShowcauseId(null);
  };

  const handleUpdateShowcause = async () => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_DOT_NET_CORE_API_URL}/Saveadvisory`, {
        id: editingShowcauseId.toString(),
        appId: appId,
        description: showcauseText.trim(),
        labtypeid: applicationData?.LabTypeId || "0",
        updatedby: localStorage.getItem('orgId') || '0',
        advisoryType: 'ShowCause',
        type: 'Update'
      });

      if (response.data && response.data.isstatus) {
        showSnackbar('Showcause updated successfully', 'success');
        setShowcauseText('');
        setEditingShowcauseId(null);
        await loadAdvisoryList();
      } else {
        showSnackbar('Failed to update showcause', 'error');
      }
    } catch (err) {
      console.error('Error updating showcause:', err);
      showSnackbar('Failed to update showcause', 'error');
    }
  };

  const handleDeleteAdvisory = async (id) => {
    if (!window.confirm('Do you want to delete this advisory?')) {
      return;
    }

    try {
      const response = await axios.post(`${process.env.REACT_APP_DOT_NET_CORE_API_URL}/Deleteadvisory?id=${id.toString()}&appid=${appId}&updatedby=${localStorage.getItem('orgId') || '0'}`);

      if (response.data && response.data.isstatus) {
        showSnackbar('Advisory deleted successfully', 'success');
        // Reload advisory list after successful deletion
        await loadAdvisoryList();
      } else {
        showSnackbar(response.data?.message || 'Failed to delete advisory', 'error');
      }
    } catch (err) {
      console.error('Error deleting advisory:', err);
      showSnackbar(err.response?.data?.message || 'Failed to delete advisory', 'error');
    }
  };

  const handleSubmit = () => {
    // Validate required fields
    if (!communicationSubject.trim()) {
      showSnackbar('Please enter Communication Letter Subject', 'warning');
      return;
    }
    if (!communicationBody.trim()) {
      showSnackbar('Please enter Communication Letter Body', 'warning');
      return;
    }

    setShowPasswordModal(true);
  };

  const handlePasswordSubmit = async () => {
    if (!password.trim()) {
      showSnackbar('Please enter password', 'warning');
      return;
    }

    try {
      // Verify password first
      const passwordResponse = await axios.post(`${process.env.REACT_APP_API_URL}/auth/verify-password`, {
        orgId: localStorage.getItem('orgId') || '0',
        password: password
      });
      
      if (!passwordResponse.data.success) {
        showSnackbar('Invalid password', 'error');
        return;
      }

      // Save communication
      const response = await axios.post(`${process.env.REACT_APP_DOT_NET_CORE_API_URL}/SaveCommunication`, {
        appId: appId,
        orgid: orgId,
        subject: communicationSubject.trim(),
        body: communicationBody.trim()
      });
      
      if (response.data && response.data.isstatus) {
        showSnackbar('Decision submitted successfully', 'success');
        setShowPasswordModal(false);
        setPassword('');
        
        // Update button visibility after successful save
        updateButtonVisibility(communicationSubject.trim(), communicationBody.trim());
      } else {
        showSnackbar('Failed to submit decision', 'error');
      }
    } catch (err) {
      console.error('Error submitting decision:', err);
      showSnackbar(err.response?.data?.message || 'Invalid password', 'error');
    }
  };

  const handleViewLetter = async () => {
    try {
      showSnackbar('Generating communication letter...', 'info');
      //const doNameEmailData = await CommonAPIService.getDoNameEmail(appId, orgId);
      //const applicationData = doNameEmailData.data || {};
      // Check if application data is available
      if (!applicationData) {
        showSnackbar('Application data not loaded. Please wait and try again.', 'warning');
        return;
      }

      // Prepare letter data
      const letterData = {
        applicationNo: applicationData.ApplicationNo || 'N/A',
        letterDate: communicationDate ? new Date(communicationDate).toLocaleDateString('en-GB') : '',
        contactPerson: applicationData.ContactPerson || 'N/A',
        laboratoryName: applicationData.LaboratoryName || 'N/A',
        address: applicationData.Address || 'N/A',
        extAddress: applicationData.ExtAddress || 'N/A',
        mobile: applicationData.ContactMobile || 'N/A',
        email: applicationData.ContactEmail || 'N/A',
        subject: communicationSubject || 'N/A',
        body: communicationBody || 'N/A',
        doName: applicationData.DoName || 'N/A',
        doEmail: applicationData.DoEmail || 'N/A',
        showAdverseNote: '',
        showNewAddress: new Date() > new Date('2025-07-18')
      };

      // Generate PDF
      await generateCommunicationLetter(letterData);
      
      showSnackbar('Communication letter downloaded successfully', 'success');
    } catch (err) {
      console.error('Error generating letter:', err);
      showSnackbar('Failed to generate communication letter', 'error');
    }
  };

  const handleSendLetter = () => {
    if(advisoryList.length == 0 && ddlStatus && (ddlStatus === 'Accreditation With Advisory' || ddlStatus === 'Accredited with advisory including show cause notice' || ddlStatus=== 'Show Cause' )){
      showSnackbar('Please Fill Advisory/Show Cause', 'warning')
    } else {
      setShowSendLetterModal(true);
    }
  };

  const handleSendLetterSubmit = async () => {
    if (!sendLetterPassword.trim()) {
      showSnackbar('Please enter password', 'warning');
      return;
    }

    try {
      // Verify password first
      const passwordResponse = await axios.post(`${process.env.REACT_APP_API_URL}/auth/verify-password`, {
        orgId: localStorage.getItem('orgId') || '0',
        password: sendLetterPassword
      });
      
      if (!passwordResponse.data.success) {
        showSnackbar('Invalid password', 'error');
        return;
      }

      // Send communication
      const response = await axios.post(`${process.env.REACT_APP_DOT_NET_CORE_API_URL}/Sendcommunication`, {
        appId: appId,
        updatedby: localStorage.getItem('orgId') || '0',
        orgid: orgId
      });
      
      if (response.data && response.data.isstatus) {
        // Mark isdecisionSent = 1 so lab can now view the decision letter
        try {
          await axios.put(`${process.env.REACT_APP_API_URL}/ac-process/decision/mark-sent`, { appId });
        } catch (markErr) {
          console.error('Failed to mark decision sent flag:', markErr);
        }

        showSnackbar('Letter sent successfully', 'success');
        setShowSendLetterModal(false);
        setSendLetterPassword('');

        // Navigate back after delay
        setTimeout(() => {
          navigate(`/main/do/ac-process?appId=${encryptAppId(appId)}&orgId=${encryptText(orgId)}&assessmentId=${encryptText(assessmentId)}&status=${encryptText(ddlStatus)}`);
        }, 1500);
      }else {
        showSnackbar('Failed to send letter', 'error');
      }
    } catch (err) {
      console.error('Error sending letter:', err);
      showSnackbar(err.response?.data?.message || 'Invalid password', 'error');
    }
  };

  const advisoryOnlyList = advisoryList.filter((a) => a.advisoryType === 'Advisory');
  const showcauseOnlyList = advisoryList.filter((a) => a.advisoryType === 'ShowCause');

  // Render error state
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button 
          onClick={() => navigate(`/main/do/ac-process?appId=${encryptAppId(appId)}&orgId=${encryptText(orgId)}&assessmentId=${encryptText(assessmentId)}&status=${encryptText(ddlStatus)})}`)} 
          startIcon={<ArrowBack />} 
          sx={{ mt: 2 }}
        >
          Go Back
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, maxWidth: 1400, mx: "auto" }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          background: "linear-gradient(135deg, #00024c 0%, #1a2b6b 100%)",
          color: "white",
          borderRadius: 0,
          mb: 3,
        }}
      >
        <Box sx={{ px: 3, py: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)", mr: 2, width: 56, height: 56 }}>
                <Assignment sx={{ fontSize: 28 }} />
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Decision Process
                </Typography>
                <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                  {assessmentTypeId === '5' ? "Create Advisory" : "Communication Letter"}
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              startIcon={<ArrowBack />}
              onClick={() => navigate(`/main/do/ac-process?appId=${encryptAppId(appId)}&orgId=${encryptText(orgId)}&assessmentId=${encryptText(assessmentId)}&status=${encryptText(ddlStatus)})`)}
              sx={{
                bgcolor: "rgba(255,255,255,0.2)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.3)" }
              }}
            >
              Back
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Main Content */}
      <Paper elevation={3} sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {/* Advisory Section */}
          {showAdvisorySection && (
            <>
              <Grid item xs={12}>
                <Alert severity="info">
                  Create points on which reply to be sought from laboratory like advisory
                </Alert>
              </Grid>

              <Grid item xs={12}>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Enter Advisory Points
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    value={advisoryText}
                    onChange={(e) => setAdvisoryText(e.target.value)}
                    placeholder="Enter points.."
                    sx={{ mb: 2 }}
                  />
                  <Button
                    variant="contained"
                    startIcon={editingAdvisoryId ? <Edit /> : <Add />}
                    onClick={editingAdvisoryId ? handleUpdateAdvisory : handleAddAdvisory}
                  >
                    {editingAdvisoryId ? 'Update' : 'Add'}
                  </Button>
                  {editingAdvisoryId && (
                  <Button
                    variant="contained"
                    startIcon={<Cancel />}
                    sx={{ ml: 2 }}
                    onClick={cancelEditAdvisory}
                  >
                    Cancel Edit
                  </Button>
                  )}
                </Card>
              </Grid>

              {/* Advisory List */}
              {advisoryOnlyList.length > 0 && (
                <Grid item xs={12}>
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Sr. No.</TableCell>
                          <TableCell>Advisory</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {advisoryOnlyList.map((advisory, index) => (
                          <TableRow key={advisory.id}>
                            <TableCell>{index + 1}</TableCell>
                            <TableCell>{advisory.description}</TableCell>
                            <TableCell>
                              <IconButton
                                color="primary"
                                size="small"
                                onClick={() => handleEditAdvisory(advisory)}
                              >
                                <Edit />
                              </IconButton>
                              <IconButton
                                color="error"
                                size="small"
                                onClick={() => handleDeleteAdvisory(advisory.id)}
                              >
                                <Delete />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              )}
            </>
          )}

          {/* Showcause Section */}
          {showShowcauseSection && (
            <>
              <Grid item xs={12}>
                <Alert severity="info">
                  Create points on which reply to be sought from laboratory like showcause
                </Alert>
              </Grid>

              <Grid item xs={12}>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Enter Showcause Points
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    value={showcauseText}
                    onChange={(e) => setShowcauseText(e.target.value)}
                    placeholder="Enter points.."
                    sx={{ mb: 2 }}
                  />
                  <Button
                    variant="contained"
                    startIcon={editingShowcauseId ? <Edit /> : <Add />}
                    onClick={editingShowcauseId ? handleUpdateShowcause : handleAddShowcause}
                  >
                    {editingShowcauseId ? 'Update' : 'Add'}
                  </Button>
                  {editingShowcauseId && (
                  <Button
                    variant="contained"
                    startIcon={<Cancel />}
                    sx={{ ml: 2 }}
                    onClick={cancelEditShowcause}
                  >
                    Cancel Edit
                  </Button>
                  )}
                </Card>
              </Grid>

              {/* Showcause List */}
              {showcauseOnlyList.length > 0 && (
                <Grid item xs={12}>
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Sr. No.</TableCell>
                          <TableCell>Showcause</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {showcauseOnlyList.map((showcause, index) => (
                          <TableRow key={showcause.id}>
                            <TableCell>{index + 1}</TableCell>
                            <TableCell>{showcause.description}</TableCell>
                            <TableCell>
                              <IconButton
                                color="primary"
                                size="small"
                                onClick={() => handleEditShowcause(showcause)}
                              >
                                <Edit />
                              </IconButton>
                              <IconButton
                                color="error"
                                size="small"
                                onClick={() => handleDeleteAdvisory(showcause.id)}
                              >
                                <Delete />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              )}
            </>
          )}

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
          </Grid>

          {/* Communication Letter Fields */}
          {['1','2'].includes(assessmentTypeId) && (
            <>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" gutterBottom>
                Communication Letter
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                startIcon={<Edit />}
                onClick={() => navigate(`/main/do/lab-details?appId=${encryptAppId(appId)}&orgId=${encryptText(orgId)}&assessmentId=${encryptText(assessmentId)}`)}
              >
                Edit Contact Details
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12} md={12}>
            <TextField
              fullWidth
              required
              label="Enter Communication Letter Subject"
              value={communicationSubject}
              onChange={(e) => setCommunicationSubject(e.target.value)}
              placeholder="Subject"
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Enter Communication Letter Body *
            </Typography>
            <Box sx={{ border: '1px solid #ccc', borderRadius: 1, minHeight: 300 }}>
              <ReactQuill
                theme="snow"
                value={communicationBody}
                onChange={setCommunicationBody}
                style={{ height: 250 }}
              />
            </Box>
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12} sx={{ textAlign: "center", mt: 4 }}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              {showSubmitButton && (
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handleSubmit}
                >
                  Submit
                </Button>
              )}
              
              {showViewLetterButton && (
                <Button
                  variant="contained"
                  color="info"
                  size="large"
                  startIcon={<Visibility />}
                  onClick={handleViewLetter}
                >
                  View Communication Letter
                </Button>
              )}
              
              {showSendLetterButton && (
                <Button
                  variant="contained"
                  color="success"
                  size="large"
                  startIcon={<Send />}
                  onClick={handleSendLetter}
                >
                  Send Letter
                </Button>
              )}
            </Box>
          </Grid>
          </>
          )}
        </Grid>
      </Paper>

      {/* Password Modal for Submit */}
      <Dialog
        open={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Submit Decision</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Enter Your Password to Continue.
          </Typography>
          <TextField
            fullWidth
            type="password"
            label="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPasswordModal(false)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handlePasswordSubmit}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Modal for Send Letter */}
      <Dialog
        open={showSendLetterModal}
        onClose={() => setShowSendLetterModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Send Letter to CAB</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Enter Your Password to Continue.
          </Typography>
          <TextField
            fullWidth
            type="password"
            label="Password"
            value={sendLetterPassword}
            onChange={(e) => setSendLetterPassword(e.target.value)}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSendLetterModal(false)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleSendLetterSubmit}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DecisionProcess;
